import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import CreateOfferModal from '../components/CreateOfferModal';

const Offers = ({ isDarkMode }) => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        try {
            const res = await api.get('/restaurant/coupons');
            setCoupons(res.data.coupons || []);
        } catch (error) {
            console.error('Failed to fetch coupons', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this coupon?')) return;
        try {
            await api.delete(`/restaurant/coupons/${id}`);
            fetchCoupons();
        } catch (error) {
            console.error('Failed to delete coupon', error);
        }
    };

    const styles = getStyles(isDarkMode);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h2 style={styles.title}>Offers & Coupons</h2>
                    <p style={styles.subtitle}>Manage promo codes and discounts</p>
                </div>
                <button style={styles.createBtn} onClick={() => setIsModalOpen(true)}>
                    + Create New Offer
                </button>
            </div>

            {loading ? (
                <div style={{ padding: '20px' }}>Loading...</div>
            ) : (
                <div style={styles.grid}>
                    {coupons.length === 0 && (
                        <div style={styles.emptyState}>
                            No coupons found. Create your first offer!
                        </div>
                    )}
                    {coupons.map(coupon => (
                        <div key={coupon.id} style={styles.card}>
                            <div style={styles.cardHeader}>
                                <span style={styles.code}>{coupon.code}</span>
                                <span style={{
                                    ...styles.statusBadge,
                                    backgroundColor: coupon.isActive ? '#e6fffa' : '#fff5f5',
                                    color: coupon.isActive ? '#2c7a7b' : '#c53030'
                                }}>
                                    {coupon.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div style={styles.cardBody}>
                                <div style={styles.discount}>
                                    {coupon.discountType === 'PERCENTAGE'
                                        ? `${coupon.discountValue}% OFF`
                                        : `₹${coupon.discountValue} OFF`
                                    }
                                </div>
                                <p style={styles.description}>{coupon.description}</p>
                                <div style={styles.meta}>
                                    <div>Type: {coupon.discountType}</div>
                                    {coupon.minOrderValue > 0 && <div>Min Order: ₹{coupon.minOrderValue}</div>}
                                    {coupon.maxDiscount > 0 && <div>Max Disc: ₹{coupon.maxDiscount}</div>}
                                    <div>Usage: {coupon.usageCount} / {coupon.usageLimit === 0 ? '∞' : coupon.usageLimit}</div>
                                </div>
                            </div>
                            <div style={styles.cardFooter}>
                                <button style={styles.deleteBtn} onClick={() => handleDelete(coupon.id)}>Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <CreateOfferModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onCreate={fetchCoupons}
                isDarkMode={isDarkMode}
            />
        </div>
    );
};

const getStyles = (isDark) => ({
    container: {
        padding: '24px 32px',
        color: isDark ? '#fff' : '#333'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    title: {
        margin: 0,
        fontSize: '1.8rem',
        fontWeight: '700'
    },
    subtitle: {
        margin: '4px 0 0 0',
        color: isDark ? '#888' : '#666',
        fontSize: '0.9rem'
    },
    createBtn: {
        padding: '10px 20px',
        backgroundColor: '#0984E3',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px'
    },
    card: {
        backgroundColor: isDark ? '#1E1E24' : '#fff',
        borderRadius: '12px',
        padding: '20px',
        border: isDark ? '1px solid #333' : '1px solid #e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    code: {
        fontSize: '1.2rem',
        fontWeight: '700',
        letterSpacing: '1px',
        color: isDark ? '#fff' : '#2D3436',
        backgroundColor: isDark ? '#333' : '#f0f0f0',
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px dashed #aaa'
    },
    statusBadge: {
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600'
    },
    cardBody: {
        marginBottom: '16px'
    },
    discount: {
        fontSize: '1.5rem',
        fontWeight: '800',
        color: '#0984E3',
        marginBottom: '8px'
    },
    description: {
        fontSize: '0.9rem',
        color: isDark ? '#ccc' : '#666',
        marginBottom: '12px',
        minHeight: '20px'
    },
    meta: {
        fontSize: '0.8rem',
        color: isDark ? '#888' : '#888',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px 16px'
    },
    cardFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        borderTop: isDark ? '1px solid #333' : '1px solid #f0f0f0',
        paddingTop: '12px'
    },
    deleteBtn: {
        background: 'none',
        border: 'none',
        color: '#ff4757',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: '500'
    },
    emptyState: {
        gridColumn: '1 / -1',
        textAlign: 'center',
        padding: '40px',
        color: isDark ? '#666' : '#999',
        fontSize: '1.1rem'
    }
});

export default Offers;
